import permissionService from 'author/src/services/permissions/PermissionService';
import { ContextState } from 'author/src/store/context/reducer';
import { CustomPermissions } from 'author/src/store/context/types';
import { HitData } from '../../store/common/search/types';
import { StoreManager, CommonRootState } from '../../store/typings';
import commonWorkspaceSchemeService from '../commonWorkspaceSchemeService';
import { WorkspacesOverlayTypes } from '../../store/workspaces/types';
import { CustomPrivileges, SessionState, UserObject } from '../../store/session/reducer';
import {
    FeatureTypesWithCurs, ExtendedAccessFeatures, FeaturePrivilegesWithCurs,
} from './typings';
import { getUrlParam } from '../../util/url/urlUtil';
import { BlocklistWorkspaces, ContentTypes, ContentTypesTemplates } from '../../store/common/constants';
import { isQuestionSetTemplate } from '../../util/shared/sharedUtils';

export const ASSESSMENT_UI_TEST_INVIGILATOR = 'assessment_ui_testinvigilator';
export const ASSESSMENT_UI_MONITORING = 'assessment_ui_monitoring';
export const ASSESSMENT_UI_SECURITY_TOOL = 'assessment_ui_security_tool';
export interface AppSpecifiedPermissionService {
    filterColumnsByPermissions: (columns: any[], dataType: string, featureId?: string) => any[];
    hasPermissionForAction?: (action: string, indexDocument: HitData) => boolean;
}

let appSpecifiedPermissionService: AppSpecifiedPermissionService;
let store: StoreManager;

export const isWorkflowOverlayOpen = (): boolean => {
    const { workspaceOverlay } = (store.getState() as CommonRootState).workspaces;
    return workspaceOverlay && workspaceOverlay.workspaceOverlayName === WorkspacesOverlayTypes.WORKFLOW_OVERLAY;
};

export const isWorkflowSettingsOverlayOpen = () => {
    const { workspaceOverlay } = (store.getState() as CommonRootState).workspaces;
    return workspaceOverlay && workspaceOverlay.workspaceOverlayName === WorkspacesOverlayTypes.WORKFLOW_SETTINGS;
};

export const getWorkspaceType = (): string => {
    const { workspaces, context } = (store.getState() as CommonRootState);
    const { workspaceId } = context;
    const { workspaceScheme } = workspaces;
    return commonWorkspaceSchemeService.getWorkspaceType(workspaceId, workspaceScheme);
};

export const hasAuthorAccessViaCUR = () => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    const privileges = sessionState?.user?.privileges;
    const hasAuthorPrivilege = privileges?.includes('system_role_testauthor_rel');
    const hasAuthorPrivilegeViaCur = sessionState?.user?.permissions?.includes('assessment_ui_author_tool');
    return hasAuthorPrivilegeViaCur && !hasAuthorPrivilege;
};

export const hasAuthorToolViaCUR = (): boolean => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    return Boolean(sessionState?.user?.permissions?.includes('assessment_ui_author_tool'));
};

export const getUserPrivileges = () => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    const privileges = sessionState?.user?.privileges;
    if (hasAuthorAccessViaCUR()) {
        return [...privileges, 'system_role_testauthor_rel'];
    }
    return privileges;
};

export const getSessionPrivileges = () => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    return sessionState?.user?.privileges;
};

export const hasExitLockdownPermission = (): boolean => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    const permissions = sessionState?.user?.permissions;
    const exitLockdownPermissions = [
        ASSESSMENT_UI_TEST_INVIGILATOR,
        ASSESSMENT_UI_MONITORING,
        ASSESSMENT_UI_SECURITY_TOOL,
    ];

    return exitLockdownPermissions.some((permission) => permissions.includes(permission));
};

export const getBlocklistAdminPermission = () => {
    const state: CommonRootState = store.getState();
    const blocklistWorkspaces = [
        BlocklistWorkspaces.DEVICE_IDS_BLOCKLIS,
        BlocklistWorkspaces.SOFTWARE_BLOCKLIST,
    ];
    const searchLocation = state?.router?.location;
    const isMatchInQuery = blocklistWorkspaces.includes(searchLocation?.query?.workspaceId);
    const isMatchInPathname = blocklistWorkspaces.some(
        (workspace: string) => searchLocation?.pathname.includes(workspace)
    );
    return isMatchInQuery || isMatchInPathname;
};

export const initializeCommonPermissionService = (appStore: StoreManager,
    appPermissionService: AppSpecifiedPermissionService): void => {
    store = appStore;
    appSpecifiedPermissionService = appPermissionService;
};

export const getAppSpecifiedPermissionService = (): AppSpecifiedPermissionService => {
    return appSpecifiedPermissionService;
};

export const getCurPrivilegesForFeature = (feature: FeatureTypesWithCurs, type?: string) => {
    const { session, context } = store?.getState() || {};
    const { customPrivileges } = session?.user || {};
    const featurePrivileges = FeaturePrivilegesWithCurs[feature as keyof typeof FeaturePrivilegesWithCurs];
    let contentType = getUrlParam('contentType');
    const isTemplate = getUrlParam('isTemplate') === 'true';
    let userPrivilegesOnIb: CustomPrivileges[];
    const { itemBankInfo } = context || {};
    const { customUserRoles } = itemBankInfo || {};
    if (!customUserRoles) {
        userPrivilegesOnIb = customPrivileges;
    } else {
        userPrivilegesOnIb = customPrivileges?.filter(({ customUserRoleIds }) => {
            return customUserRoleIds?.some((id) => customUserRoles && Object.keys(customUserRoles)
                ?.some((role: string) => role.includes(String(id))));
        });
    }

    if (isTemplate || context?.currentItemContext?.isTemplate) {
        contentType = ContentTypesTemplates[contentType];
    }

    if (type) {
        contentType = type;
    }

    const availablePrivileges = Object.values(featurePrivileges)
        .filter((privilege) => userPrivilegesOnIb
                                ?.some(({ name }) => name === privilege[contentType]));

    return availablePrivileges.map((privilege) => privilege[contentType]);
};

/**
 * A share is granted either to the user directly or to an access group they belong to, so both have
 * to be matched against a permission's granteeIds. Mirrors ObjectHitModel.hasEditPrivilege in
 * platform-ia; leaving the access groups out makes an AG-based share invisible here, and the
 * grantee then loses access they legitimately hold.
 */
const getGranteePartyIds = (userId: number): number[] => {
    const { session } = store?.getState() || {};
    return [userId, ...(session?.user?.effectiveOrgUnitIds || [])].map(Number);
};

const isGrantedTo = (permission: CustomPermissions, granteePartyIds: number[]): boolean => {
    return !!permission.granteeIds?.some((granteeId) => granteePartyIds.includes(Number(granteeId)));
};

const getUsersCursOnCurrentItem = (
    permissionsOnContent: CustomPermissions[],
    customPrivileges: CustomPrivileges[],
    userId?: number,
) => {
    if (userId == null) {
        return [];
    }

    const granteePartyIds = getGranteePartyIds(userId);

    const rolesOnPermissions = (permissionsOnContent || []).reduce<number[]>(
        (roles, permission) => {
            if (
                isGrantedTo(permission, granteePartyIds)
                && permission.customUserRoleId != null
            ) {
                roles.push(permission.customUserRoleId);
            }
            return roles;
        },
        []
    );

    return (customPrivileges || []).filter(({ customUserRoleIds }) => customUserRoleIds?.some((role) => rolesOnPermissions.includes(role)));
};

export const getCurPrivilegesForFeatureOnCurrentItem = (
    feature: FeatureTypesWithCurs,
    customPrivileges: CustomPrivileges[],
    context: ContextState,
    contentType: string,
) => {
    const { customPermissions } = context?.currentItemContext || {};
    const { session } = store?.getState() || {};
    const userId = session?.user?.id;
    const userRoles = getUsersCursOnCurrentItem(
        customPermissions,
        customPrivileges,
        userId,
    );
    const featureRoles = FeaturePrivilegesWithCurs[feature as keyof typeof FeaturePrivilegesWithCurs];

    const availablePrivileges = Object.values(featureRoles).filter(
        (privilege) => {
            const privilegeMap = privilege as Record<string, string>;

            return userRoles?.some(
                ({ name }) => name === privilegeMap[contentType]
            );
        }
    );
    return availablePrivileges
        .map((privilege) => (privilege as Record<string, string>)[contentType])
        .filter((p): p is string => typeof p === 'string');
};


export const hasReadAtcUsageAccessViaCUR = (): boolean => {
    const { session } = store?.getState() || {};
    const { customPrivileges } = session?.user || {};
    return customPrivileges?.some((privilege) => privilege.name === 'read_atc_content_blocks_usage_lists') || false;
};

export const hasReadContentBlockAudioScriptViaCUR = (): boolean => {
    const { session } = store?.getState() || {};
    const { customPrivileges } = session?.user || {};
    return customPrivileges?.some((privilege) => privilege.name === 'read_content_block_audio_script') || false;
};

export const hasReadQuestionTestCasesViaCUR = (): boolean => {
    const { session } = store?.getState() || {};
    const { customPrivileges } = session?.user || {};
    return customPrivileges?.some((privilege) => privilege.name === 'read_question_test_cases') || false;
};

export const hasQuestionTestCasesAccess = (
    marketplaceProperties: { ENABLE_QUESTION_TEST_CASES?: boolean } = window.marketplaceProperties,
): boolean => {
    return Boolean(marketplaceProperties?.ENABLE_QUESTION_TEST_CASES) && hasReadQuestionTestCasesViaCUR();
};

const hasContributorAssignment = (
    customPermissions: CustomPermissions[] = [],
    userId?: number,
): boolean => {
    if (userId == null) {
        return false;
    }

    const granteePartyIds = getGranteePartyIds(userId);

    return customPermissions.some((permission) => permission.customUserRoleId != null
        && isGrantedTo(permission, granteePartyIds));
};

/**
 * PCIB: private content shared with a user who does not own it is governed by the CUR chosen in the share,
 * including for built-in Authors. Extended Access users keep full access.
 */
export const isPrivateContentSharedWithCurrentUser = (): boolean => {
    const { context, session } = store?.getState() || {};
    const currentItemContext = context?.currentItemContext;
    const securityInherit = currentItemContext?.securityInherit ?? context?.assessmentData?.securityInherit;
    const userId = session?.user?.id;
    const isExtendedAccessUser = Boolean(
        session?.user?.privileges?.some((role) => userHasExtendedAccess(role))
        || session?.user?.customPrivileges?.some((role) => userHasExtendedAccess(role?.name)),
    );

    if (securityInherit !== false || userId == null
        || Number(currentItemContext?.creationUserId) === Number(userId)
        || isExtendedAccessUser) {
        return false;
    }

    return hasContributorAssignment(currentItemContext?.customPermissions, userId);
};

export const hasCURAccessForAction = (
    feature: FeatureTypesWithCurs,
    action?: { [x: string]: string },
    type?: string,
    options?: { curGovernsBuiltInAuthor?: boolean },
): boolean => {
    const privileges = getCurPrivilegesForFeature(feature, type);
    const isReviewAction = ExtendedAccessFeatures.includes(feature);
    const curAccessEnabled = window.marketplaceProperties?.ENABLE_ORG_UNIT_WITH_CUR_ON_ITEM_BANK;

    const isSharedPrivateContent = isPrivateContentSharedWithCurrentUser();
    const curGovernsUser = hasAuthorAccessViaCUR()
        || (options?.curGovernsBuiltInAuthor === true && hasAuthorToolViaCUR());
    let contentType = getUrlParam('contentType');
    const isTemplate = getUrlParam('isTemplate') === 'true';
    const { context, session } = store.getState();
    const { customPrivileges } = session?.user || {};

    if (isTemplate || context?.currentItemContext?.isTemplate) {
        contentType = ContentTypesTemplates[contentType];
    }

    if (type) {
        contentType = type;
    }

    if (!isSharedPrivateContent && (!curAccessEnabled || !curGovernsUser)) {
        return isReviewAction ? !!permissionService?.hasCurrentUserAccessForReviewingTool() : true;
    }

    // This check is only for the current item, if the user has contributor assignment on the current item,
    // then we will check the privileges on the current item, otherwise we will check the privileges on the item bank.
    const hasAccessToCurrentItemThroughContributorAssignment = hasContributorAssignment(
        context?.currentItemContext?.customPermissions,
        session?.user?.id
    );

    if (hasAccessToCurrentItemThroughContributorAssignment) {
        const privilegesOnCurrentItem = getCurPrivilegesForFeatureOnCurrentItem(
            feature,
            customPrivileges,
            context,
            contentType,
        );
        return !action
            ? !!privilegesOnCurrentItem?.length
            : privilegesOnCurrentItem?.includes(action[contentType]);
    }

    if (!action) {
        return !!privileges?.length;
    }
    return privileges?.includes(action[contentType]);
};

export const hasReadAssociatedCalibrationTestEventsViaCUR = (): boolean => {
    return hasCURAccessForAction(FeatureTypesWithCurs.ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS);
};

/**
 * IB-21477 — controls every "Open in Print Manager" entry point in Author 2.0.
 *
 * Only Content Blocks and Question Sets are governed; questions and template variants keep their
 * existing behaviour. Built-in Author and Extended Access users always keep the entry point: the
 * explicit bypass below is required because hasAuthorAccessViaCUR() classes an Extended Access user
 * (who holds assessment_ui_author_tool but not system_role_testauthor_rel) as CUR governed, so
 * hasCURAccessForAction alone would hide Print Manager from them.
 */
export const hasPrintManagerAccessViaCUR = (): boolean => {
    const contentType = getUrlParam('contentType');
    const isTemplate = getUrlParam('isTemplate') === 'true';
    const isGovernedContent = !isTemplate
        && (contentType === ContentTypes.SECTION || contentType === ContentTypes.QUESTION_SET);

    if (!isGovernedContent) {
        return true;
    }

    if (permissionService?.hasAccessForAuthorOrExtendedaccess()) {
        return true;
    }

    const { READ_PRINT_MANAGER_ACCESS } = FeaturePrivilegesWithCurs[FeatureTypesWithCurs.PRINT_MANAGER_ACCESS];
    return hasCURAccessForAction(FeatureTypesWithCurs.PRINT_MANAGER_ACCESS, READ_PRINT_MANAGER_ACCESS, contentType);
};

export const hasQuestionSetTemplateWriteAccess = () => {
    const sessionState: SessionState = (store.getState() as CommonRootState).session;
    const { customPrivileges } = sessionState?.user || {};
    const hasQSTemplateEditAccess = customPrivileges?.some((item) => item.name === 'write_question_set_template');
    return hasQSTemplateEditAccess && isQuestionSetTemplate();
};

const doesUserHasReadEventLogAccess = (user?: UserObject): boolean => {
    const { customPrivileges } = user || {};
    const isCURRestrictedLogAccess = window.marketplaceProperties.ENABLE_CUR_EVENT_LOG_CONTROL;
    return (isCURRestrictedLogAccess && customPrivileges?.some(
        ({ name }) => name === 'read_event_log_access'
    )) || false;
};

const userHasExtendedAccess = (role: string): boolean => {
    return role === 'system_role_testextendedcontentadmin_rel';
};

const userHasSystemRole = (role: string): boolean => {
    const isSystemRole = !userHasCUR(role) && role?.startsWith('system_role_')
        && !userHasExtendedAccess(role);
    return isSystemRole;
};

const userHasCUR = (role: string): boolean => {
    const CUSTOM_USER_ROLE_REL_SEGMENT_REGEX = /^system_role_[0-9]+_rel$/;
    return !!CUSTOM_USER_ROLE_REL_SEGMENT_REGEX.test(role);
};

export const hasAccessToEventLogsForUser = (): boolean => {
    const { session } = store?.getState() || {};
    const user = session?.user;

    // if grading context
    if (window.location.pathname.includes('/grading')) {
        return !!user?.hasEventLogAccess;
    }

    // if student return false
    if (user?.isStudent) {
        return false;
    }
    const isExtendedAccessUser = user?.hasExtendedAccess || user?.privileges?.some((role) => userHasExtendedAccess(role));
    const isSystemRoleUser = user?.privileges?.some((role) => userHasSystemRole(role));
    const isCURUser = user?.privileges?.some((role) => userHasCUR(role));
    const hasReadEventLogAccess = doesUserHasReadEventLogAccess(user);

    // if extended user return true
    if (isExtendedAccessUser) {
        return true;
    }
    const isSystemRoleRestrictedLogAccess = window.marketplaceProperties.ENABLE_SYSTEM_ROLE_RESTRICT_LOG_ACCESS;
    const isCURRestrictedLogAccess = window.marketplaceProperties.ENABLE_CUR_EVENT_LOG_CONTROL;

    // System Role takes priority when the user has both System Role and CUR roles.
    if (isSystemRoleUser) {
        return !isSystemRoleRestrictedLogAccess || hasReadEventLogAccess;
    }
    // Evaluate CUR access only when the user has no System Role.
    if (isCURUser) {
        return !isCURRestrictedLogAccess || hasReadEventLogAccess;
    }

    return false; // Default to false if none of the above conditions are met.
};
